import type { ShopSummary } from '../../services/adminApi'

interface ShopCardProps {
  shop: ShopSummary
  onClick?: (shop: ShopSummary) => void
}

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  pending: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  verified: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  suspended: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-400' },
  closed: { bg: 'bg-gray-200 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
}

export function ShopCard({ shop, onClick }: ShopCardProps) {
  const formattedDate = new Date(shop.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const status = statusColors[shop.status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' }
  const initials = shop.name.charAt(0).toUpperCase()

  return (
    <div
      role="button"
      tabIndex={0}
      className="group relative rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-5 transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1 shadow-sm dark:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503864] dark:focus-visible:ring-[#b58fcc]"
      onClick={() => onClick?.(shop)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(shop)
        }
      }}
    >
      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-[#503864] dark:bg-[#7f4f9a] flex items-center justify-center">
              <span className="text-lg font-bold text-white">{initials}</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-[#503864] dark:group-hover:text-[#b58fcc] transition-colors">
                {shop.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{shop.owner_email}</p>
            </div>
          </div>
          <span className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            <span className="capitalize">{shop.status}</span>
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#f0e3ff] dark:border-[#3d2c4f]">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[#f9f4ff] dark:bg-[#3d2c4f]">
              <svg className="w-4 h-4 text-[#7f4f9a] dark:text-[#b58fcc]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{shop.listing_count}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Listings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[#f9f4ff] dark:bg-[#3d2c4f]">
              <svg className="w-4 h-4 text-[#7f4f9a] dark:text-[#b58fcc]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formattedDate}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
            </div>
          </div>
        </div>

        {/* Verified badge */}
        {shop.verified_at && (
          <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
            </svg>
            Verified {new Date(shop.verified_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  )
}
